import type { BusinessAccess } from '../hooks/useBusinessAccessInvitations';
import type { Event } from '../hooks/useEvents';
import { eventStatusOf } from '../hooks/useEvents';
import { toDayParam } from '../api/businessAccessCombined';
import { statusKey } from '../api/activityStats';

// SAM-942 · D2 Tenant dashboard — the widgets that need no new backend. Everything here is pure so
// it can be tested without React Query; src/hooks/useTenantDashboard.ts feeds it.

/**
 * business_access_status ids as Invitations.tsx and the backend use them. The older Dashboard code
 * (dashboardUtils.ts) still reads 3 as "in" and 6 as "out"; do not copy that mapping.
 */
export const PASS_STATUS = {
  PENDING: 1,
  CONFIRMED: 2,
  VERIFIED: 3,
  CHECKED_IN: 4,
  CHECKED_OUT: 5,
  CANCELLED: 6,
  EXPIRED: 7,
  PROCESSING: 8,
} as const;

/** Statuses of a pass that has not arrived yet. */
export const ARRIVING_STATUSES: readonly number[] = [
  PASS_STATUS.PENDING,
  PASS_STATUS.CONFIRMED,
  PASS_STATUS.VERIFIED,
];

export type DashboardPeriod = 'today' | '7d' | '30d';

export const DASHBOARD_PERIODS: readonly { id: DashboardPeriod; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

export interface PeriodWindow {
  from: Date;
  to: Date;
  /** YYYY-MM-DD, the form /business_access/stats takes in date_from / date_to. */
  dateFrom: string;
  dateTo: string;
}

/** Local-calendar window of a preset, ending today (7 days = today and the six before). */
export function periodWindow(period: DashboardPeriod, now: Date = new Date()): PeriodWindow {
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = period === '7d' ? 6 : period === '30d' ? 29 : 0;
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - back);
  return { from, to, dateFrom: toDayParam(from), dateTo: toDayParam(to) };
}

/** "26 Oct", "27 Sep": day and month name, three letters (en-GB writes «Sept», which is not the format of the rest). */
export function shortDay(d: Date): string {
  return `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })}`;
}

/** "26 Oct" or "26 Oct – 2 Nov": the month is always written out, never dd/mm. */
export function periodLabel(w: Pick<PeriodWindow, 'from' | 'to'>): string {
  const fmt = shortDay;
  const a = fmt(w.from);
  const b = fmt(w.to);
  return a === b ? a : `${a} – ${b}`;
}

/**
 * Status names of /business_access/stats `by_status` (lower-case, as statusKey() reads them). The names are the
 * same for business passes and residential invitations, which is why the counts go by name and not by id.
 */
const EXPECTED_KEYS = ['pending', 'confirmed', 'verified', 'checked_in', 'checked_out'] as const;
const VALID_KEYS = ['confirmed', 'verified', 'checked_in'] as const;
const ENTERED_KEYS = ['checked_in', 'checked_out'] as const;

export interface PassCounts {
  /** Passes of the window that someone still expects or that were used: not cancelled, expired or in payment. */
  expected: number;
  /** Expected passes that can open a door or are inside: confirmed, verified or checked in. */
  valid: number;
  /** Passes that went through: checked in or already checked out. */
  entered: number;
  /** Checked in and not out yet. */
  inside: number;
}

/** Counts of a stats answer. A pass counts on the day it starts. */
export function passCounts(byStatus: ReadonlyArray<{ status: string; count: number }>): PassCounts {
  const by = new Map<string, number>();
  byStatus.forEach((s) => by.set(statusKey(s.status), (by.get(statusKey(s.status)) ?? 0) + s.count));
  const sum = (keys: readonly string[]) => keys.reduce((t, k) => t + (by.get(k) ?? 0), 0);
  return { expected: sum(EXPECTED_KEYS), valid: sum(VALID_KEYS), entered: sum(ENTERED_KEYS), inside: by.get('checked_in') ?? 0 };
}

/** «61% of expected»; «Nothing expected» when there is nothing to take a share of. */
export function percentOf(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}% of expected` : 'Nothing expected';
}

/** Whole days since the oldest `create_date`, or undefined when no row has one. */
export function oldestAgeDays(createDates: ReadonlyArray<string | undefined | null>, now: Date = new Date()): number | undefined {
  const times = createDates.map((d) => (d ? new Date(d).getTime() : NaN)).filter((t) => !Number.isNaN(t));
  if (!times.length) return undefined;
  return Math.max(0, Math.floor((now.getTime() - Math.min(...times)) / 86_400_000));
}

/** «Oldest 3 days · review», «Oldest today · review»; the plain «Review» when no age is known. */
export function oldestPendingHint(days: number | undefined): string {
  if (days === undefined) return 'Review';
  return `Oldest ${days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`} · review`;
}

/** «just now», «2 min ago», «3 h ago». */
export function updatedAgo(updatedAt: number, now: number = Date.now()): string {
  if (!updatedAt) return '';
  const min = Math.floor(Math.max(0, now - updatedAt) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  return `${Math.floor(min / 60)} h ago`;
}

/** «Monday, October 26»: month written out. */
export function headerDate(now: Date = new Date()): string {
  return now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** The chip next to the tenant name. */
export function roleLabel(p: { isXhinolaAdmin?: boolean; isAdmin?: boolean; isTenantFrontDesk?: boolean; isPropertyManager?: boolean }): string {
  if (p.isXhinolaAdmin) return 'Xhinola admin';
  if (p.isAdmin) return 'Admin';
  if (p.isTenantFrontDesk) return 'Front Desk';
  if (p.isPropertyManager) return 'Property manager';
  return 'Staff';
}

export interface ArrivalItem {
  id: string;
  entity?: BusinessAccess['entity'];
  guest: string;
  what: string;
  /** Where the visit goes: the unit of a residential invitation, or the business of a pass. */
  where: string;
  start: string;
  status: number;
}

const guestName = (a: BusinessAccess) =>
  `${a.main_guest?.name || ''} ${a.main_guest?.last_name || ''}`.trim() || 'Guest';

/** «Marina · Tower A · 4B» for an invitation, the business name for a pass. */
export function arrivalDestination(r: BusinessAccess): string {
  if (r.entity === 'invitation') {
    return [r.destination_group, r.destination_building, r.destination_estate].filter(Boolean).join(' · ') || r.estate?.name || '';
  }
  return r.business_name || r.business?.name || '';
}

/** The estate group of a row: its estate's (invitations) or its business's estate's (passes). */
const rowEstateGroupId = (r: BusinessAccess) => r.estate?.estate_group_id ?? r.business?.estate?.estate_group_id;

/** The next passes to arrive: not arrived yet, starting from `now` on, soonest first. */
export function arrivingNext(
  rows: readonly BusinessAccess[],
  now: Date = new Date(),
  limit = 5,
  estateGroupId?: string,
): ArrivalItem[] {
  return rows
    .filter((r) => ARRIVING_STATUSES.includes(r.business_access_status_id))
    .filter((r) => !estateGroupId || String(rowEstateGroupId(r) ?? '') === estateGroupId)
    .map((r) => ({ r, t: new Date(r.start_date).getTime() }))
    .filter(({ t }) => !Number.isNaN(t) && t >= now.getTime())
    .sort((a, b) => a.t - b.t)
    .slice(0, limit)
    .map(({ r }) => ({
      id: r.id,
      entity: r.entity,
      guest: guestName(r),
      what: r.type_name || 'Pass',
      where: arrivalDestination(r),
      start: r.start_date,
      status: r.business_access_status_id,
    }));
}

export interface NextEventItem {
  id: string;
  name: string;
  start: string;
  end?: string;
  capacity?: number;
}

const eventName = (e: Event) => e.name_en || e.name_es || e.name || 'Event';

/** Published events that have not ended, soonest start first (one that is running now comes first). */
export function nextEvents(events: readonly Event[], now: Date = new Date(), limit = 5): NextEventItem[] {
  return events
    .filter((e) => eventStatusOf(e) === 'published' && !!e.start_date)
    .map((e) => ({ e, start: new Date(e.start_date as string).getTime(), end: e.end_date ? new Date(e.end_date).getTime() : NaN }))
    .filter(({ start, end }) => !Number.isNaN(start) && (Number.isNaN(end) ? start : end) >= now.getTime())
    .sort((a, b) => a.start - b.start)
    .slice(0, limit)
    .map(({ e }) => ({ id: e.id, name: eventName(e), start: e.start_date as string, end: e.end_date, capacity: e.capacity }));
}

/** "26 Oct, 3:30 PM" — month written out. */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = shortDay(d);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}
